import React, { MouseEvent, useState } from "react";
import {
  Box,
  BoxProps,
  Breakpoint,
  Button,
  ButtonProps,
  Collapse,
  IconButton,
  Link,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import InfoIcon from "@icons/myicons/info-filled.svg?react";
import InfoGreyIcon from "@icons/myicons/info.svg?react";
import WarningIcon from "@icons/myicons/warning-circle-filled.svg?react";
import SuccessIcon from "@icons/myicons/check-circle-filled.svg?react";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";
import CloseIcon from "@icons/myicons/close.svg?react";
import ArrowDown from "@icons/myicons/arrow-down.svg?react";

const SHOWED_TEXT_LENGTH = 150;

const BannerContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "breakpoint" && prop !== "alignBannerItems",
})<{
  type: string;
  breakpoint: number | Breakpoint;
  alignBannerItems: string;
}>(({ theme, type, breakpoint, alignBannerItems }) => ({
  display: "flex",
  position: "relative",
  alignItems: alignBannerItems,
  padding: theme.spacing(3, 5),
  backgroundColor: getColor(type, theme),
  boxSizing: "border-box",
  borderRadius: theme.shape.borderRadius,
  border:
    type === "white" ? `1px solid ${getBorderColor(type, theme)}` : "none",

  [theme.breakpoints.down(breakpoint)]: {
    alignItems: "center",
  },
}));

const ButtonsContainer = styled(Box)<{ shouldBreak?: boolean }>(
  ({ theme }) => ({
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: theme.spacing(4),
  })
);

const InnerBannerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "breakpoint",
})<{ breakpoint: number | Breakpoint }>(({ theme, breakpoint }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",

  [theme.breakpoints.down(breakpoint)]: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
}));

const getColor = (type: string, theme: Theme) => {
  switch (type) {
    case "grey-info":
      return theme.palette.background.neutral;
    case "warn":
      return theme.palette.background.warning;
    case "white":
      return theme.palette.background.default;
    case "error":
      return theme.palette.background.error;
    case "success":
      return theme.palette.background.success;
    default:
      return theme.palette.background.info;
  }
};

const getBorderColor = (type: string, theme: Theme) => {
  switch (type) {
    case "grey-info":
      return theme.palette.border.secondary;
    case "warn":
      return theme.palette.warning.light;
    case "white":
      return theme.palette.border.primary;
    case "error":
      return theme.palette.error.light;
    case "success":
      return theme.palette.success.light;
    default:
      return theme.palette.border.primary;
  }
};

const getIcon = (type: string) => {
  switch (type) {
    case "grey-info":
    case "white":
      return <InfoGreyIcon />;
    case "error":
      return <ErrorIcon />;
    case "warn":
      return <WarningIcon />;
    case "success":
      return <SuccessIcon />;
    default:
      return <InfoIcon />;
  }
};

const getIconColor = (type: string, theme: Theme) => {
  switch (type) {
    case "grey-info":
      return theme.palette.icon.secondary;
    case "warn":
      return theme.palette.icon.warning;
    case "white":
      return theme.palette.icon.secondary;
    case "error":
      return theme.palette.icon.error;
    case "success":
      return theme.palette.icon.success;
    default:
      return theme.palette.icon.active;
  }
};

const getSecondaryButtonColor = (type: string, theme: Theme) => {
  switch (type) {
    case "grey-info":
      return theme.palette.text.secondary;
    case "warn":
      return theme.palette.warning.main;
    case "white":
      return theme.palette.text.secondary;
    case "error":
      return theme.palette.error.main;
    case "success":
      return theme.palette.success.main;
    default:
      return theme.palette.primary.main;
  }
};

const getShowMoreButtonColor = (type: string, theme: Theme) => {
  switch (type) {
    case "grey-info":
      return theme.palette.text.primary;
    case "warn":
      return theme.palette.warning.main;
    case "white":
      return theme.palette.text.primary;
    case "error":
      return theme.palette.error.main;
    case "success":
      return theme.palette.success.main;
    default:
      return theme.palette.primary.main;
  }
};

type RequiredStringOrNot<T> = T extends false
  ? {
      id?: string;
    }
  : {
      id: string;
    };

export type BannerProps<T extends boolean> = BoxProps & {
  type: "info" | "grey-info" | "warn" | "error" | "white" | "success";
  icon?: React.ReactNode;
  title?: React.ReactNode;
  text?: React.ReactNode;
  primaryButton?: {
    label: string;
    onClick: () => void;
    testId?: string;
    variant?: ButtonProps["variant"];
    color?: ButtonProps["color"];
  };
  secondaryButton?: {
    label: string;
    onClick: (event: MouseEvent<HTMLElement>) => void;
    testId?: string;
    color?: ButtonProps["color"];
  };
  breakpoint?: number | Breakpoint;
  alignBannerItems?: string;
  hideable?: T;
  endAdornment?: React.ReactNode;
  displayDefaultIcon?: boolean;
  timePointToHide?: Date;
  onCollapse?: () => void;
  collapsed?: boolean;
} & RequiredStringOrNot<T>;

export const Banner = <T extends boolean>({
  type = "info",
  icon,
  title,
  text,
  primaryButton,
  secondaryButton,
  endAdornment,
  breakpoint = "sm",
  hideable,
  alignBannerItems = "center",
  displayDefaultIcon = false,
  timePointToHide,
  onCollapse,
  collapsed,
  ...props
}: BannerProps<T>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isDisplayed, setIsDisplayed] = useState(
    !(hideable && sessionStorage.getItem(`banner-${props.id}`))
  );
  const isMobile = useMediaQuery(theme.breakpoints.down(breakpoint));
  const [isShowedMore, setIsShowedMore] = useState(false);

  const isPastTimePoint = timePointToHide
    ? new Date() > timePointToHide
    : false;

  const isTextShouldClamp =
    typeof text === "string" &&
    text.length - 10 > SHOWED_TEXT_LENGTH &&
    isMobile &&
    !isShowedMore;

  const showedText = isTextShouldClamp
    ? `${text.slice(0, SHOWED_TEXT_LENGTH)}..`
    : text;

  const isShowMoreBtnShown = isTextShouldClamp;

  const hideInLocalStorage = () => {
    sessionStorage.setItem(`banner-${props.id}`, "true");
    setIsDisplayed(false);
  };

  if (!isDisplayed || isPastTimePoint) return null;
  return (
    <BannerContainer
      type={type}
      breakpoint={breakpoint}
      alignBannerItems={alignBannerItems}
      {...props}
    >
      {onCollapse && (
        <ArrowDown
          onClick={() => onCollapse()}
          style={{
            top: theme.spacing(2.5),
            right: theme.spacing(2.5),
            height: "30px",
            width: "30px",
            position: "absolute",
            transform: !collapsed ? "rotate(180deg)" : "rotate(0deg)",
            transformOrigin: "center center",
            transition: "transform 0.3s",
            cursor: "pointer",
          }}
        />
      )}

      <Box
        display="flex"
        alignItems="center"
        color={getIconColor(type, theme)}
        sx={{
          "& svg": {
            flexShrink: 0,
            marginRight: theme.spacing(4.5),
          },
        }}
      >
        {displayDefaultIcon ? getIcon(type) : icon}
      </Box>
      <InnerBannerContainer breakpoint={breakpoint}>
        <Box
          sx={{
            marginRight: theme.spacing(4.5),
            [theme.breakpoints.down(breakpoint)]: {
              marginBottom: theme.spacing(
                primaryButton || secondaryButton ? 3 : 0
              ),
            },
          }}
        >
          {title && (
            <Typography
              variant="body1"
              sx={{ fontWeight: 600 }}
              whiteSpace="pre-line"
            >
              {title}
            </Typography>
          )}
          <Collapse in={!collapsed}>
            {typeof text === "string" ? (
              <>
                <Typography
                  color="textSecondary"
                  variant="body3"
                  fontWeight="500"
                >
                  {showedText}
                  {isShowMoreBtnShown && (
                    <Typography
                      variant="body3"
                      component="span"
                      color={getShowMoreButtonColor(type, theme)}
                      onClick={() => setIsShowedMore(true)}
                      ml={1}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          color: getSecondaryButtonColor(type, theme),
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {t("showMore")}
                    </Typography>
                  )}
                </Typography>
              </>
            ) : (
              text
            )}
          </Collapse>
        </Box>
        {Boolean(primaryButton || secondaryButton || hideable) && (
          <ButtonsContainer>
            {primaryButton && (
              <Button
                variant={primaryButton.variant || "outlined"}
                color={primaryButton.color || "grey"}
                data-testid={primaryButton.testId || "banner-primary-button"}
                size="small"
                onClick={primaryButton.onClick}
                sx={{ flexShrink: 0 }}
              >
                {primaryButton.label}
              </Button>
            )}
            {secondaryButton && (
              <Link
                onClick={secondaryButton?.onClick}
                underline="none"
                color={
                  secondaryButton?.color || getSecondaryButtonColor(type, theme)
                }
                data-testid={
                  secondaryButton
                    ? secondaryButton.testId
                    : "banner-secondary-button"
                }
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    color:
                      secondaryButton?.color ||
                      getSecondaryButtonColor(type, theme),
                    textDecoration: "underline",
                  },
                }}
              >
                {secondaryButton?.label}
              </Link>
            )}
          </ButtonsContainer>
        )}
        {endAdornment}
      </InnerBannerContainer>
      {hideable && (
        <IconButton onClick={hideInLocalStorage}>
          <CloseIcon />
        </IconButton>
      )}
    </BannerContainer>
  );
};
